// Servicio para el Reporte 360 v2 - Lotes y Ciclos
// Cada función es una llamada independiente al backend.
// Las secciones del reporte consumen solo lo que necesitan, sin querys monolíticas.

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const handleResponse = async (response, errorMsg) => {
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`${errorMsg} (HTTP ${response.status}): ${text || response.statusText}`);
  }
  return response.json();
};

// Normaliza el rango de fechas como hace ReportsPage (YYYY-MM-DD -> local midnight en UTC)
const fromISODateOnly = (iso) => {
  const s = String(iso || '').slice(0, 10);
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

export const buildDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return null;
  const s = fromISODateOnly(startDate);
  const e = fromISODateOnly(endDate);
  const endOfDay = new Date(e);
  endOfDay.setHours(23, 59, 59, 999);
  return {
    $gte: new Date(s.getFullYear(), s.getMonth(), s.getDate()).toISOString(),
    $lte: endOfDay.toISOString(),
  };
};

// === Sección 1: KPIs (totales de recepción + ciclos de lavado) ===
export const fetchKpisCuadre = async ({ idLavanderia, dateRange }) => {
  const query = { 'lavanderia.id': idLavanderia, createdAt: dateRange };

  const [receptionResp, cyclesResp, receptionDailyResp] = await Promise.all([
    fetch(`${API_URL}/reception/reports/totals`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query }),
    }),
    // Traemos TODOS los ciclos del periodo (abortados y no abortados) y calculamos
    // los totales en el front. NO usamos `/washing-cycles/reports/totals`: ese
    // endpoint suma el campo `ciclos` de TODOS los documentos sin distinguir
    // `abortado`, y el registro de un ciclo abortado es solo una referencia manual
    // del contador (no un conteo que se pueda sumar sobre el total de la máquina),
    // así que usarlo duplicaría ciclos.
    fetch(`${API_URL}/washing-cycles/filter`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        limit: 10000,
        page: 1,
        query: { 'lavanderia.id': idLavanderia, fecha: dateRange },
      }),
    }),
    // Serie diaria de recepciones: nos da los días del periodo que sí tuvieron
    // lotes registrados (para calcular "días operados" real, no días calendario).
    fetch(`${API_URL}/reception/reports/daily`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, groupBy: 'day' }),
    }),
  ]);

  const [reception, cycles, receptionDaily] = await Promise.all([
    handleResponse(receptionResp, 'Error al obtener totales de recepción'),
    handleResponse(cyclesResp, 'Error al obtener ciclos de lavado del periodo'),
    handleResponse(receptionDailyResp, 'Error al obtener la serie diaria de recepciones'),
  ]);

  return {
    reception: reception?.data || {},
    cycles: Array.isArray(cycles?.data) ? cycles.data : [],
    // Serie diaria completa (fecha, count, pesoKg, etc.) — se usa tanto para
    // "días operados" como para las gráficas por día.
    receptionDaily: Array.isArray(receptionDaily?.data) ? receptionDaily.data : [],
  };
};

// === Sección 2: Abortados (detalle con causas) ===
export const fetchAbortados = async ({ idLavanderia, dateRange }) => {
  const query = { 'lavanderia.id': idLavanderia, fecha: dateRange, abortado: true };
  const response = await fetch(`${API_URL}/washing-cycles/filter`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ limit: 10000, page: 1, query }),
  });
  const json = await handleResponse(response, 'Error al obtener ciclos abortados');
  return Array.isArray(json?.data) ? json.data : [];
};

// === Sección 3: Detalle comparativo por máquina ===
// El endpoint de participación por máquina del backend no separa abortados
// (mezcla todo en un solo total), así que traemos los lotes y los ciclos en
// crudo y agrupamos por máquina en el front, donde sí podemos distinguir
// `abortado === true` de los ciclos válidos.
export const fetchParticipacion = async ({ idLavanderia, dateRange }) => {
  const [receptionResp, cyclesResp] = await Promise.all([
    fetch(`${API_URL}/reception/filter`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        limit: 10000,
        page: 1,
        query: { 'lavanderia.id': idLavanderia, createdAt: dateRange },
      }),
    }),
    fetch(`${API_URL}/washing-cycles/filter`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        limit: 10000,
        page: 1,
        query: { 'lavanderia.id': idLavanderia, fecha: dateRange },
      }),
    }),
  ]);

  const [reception, cycles] = await Promise.all([
    handleResponse(receptionResp, 'Error al obtener lotes por máquina'),
    handleResponse(cyclesResp, 'Error al obtener ciclos por máquina'),
  ]);

  return {
    lotes: Array.isArray(reception?.data) ? reception.data : [],
    cycles: Array.isArray(cycles?.data) ? cycles.data : [],
  };
};

// === Sección 4: Detalle de diferencias (ciclos sin lote + abortados sin causa) ===
export const fetchDetalleDiferencias = async ({ idLavanderia, dateRange }) => {
  // Trae todos los ciclos del periodo para encontrar los "sin lote" y abortados sin causa
  const query = { 'lavanderia.id': idLavanderia, fecha: dateRange };
  const response = await fetch(`${API_URL}/washing-cycles/filter`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ limit: 10000, page: 1, query }),
  });
  const json = await handleResponse(response, 'Error al obtener detalle de diferencias');
  const items = Array.isArray(json?.data) ? json.data : [];
  return items;
};

// === Envío del reporte por correo (PDF generado y enviado por el backend) ===
export const sendLotesVsCiclosReportEmail = async ({ idLavanderia, startDate, endDate, recipients, observacion }) => {
  const response = await fetch(`${API_URL}/reportes-v2/lotes-vs-ciclos/send-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      idLavanderia,
      startDate: String(startDate).slice(0, 10),
      endDate: String(endDate).slice(0, 10),
      recipients,
      observacion,
    }),
  });
  return handleResponse(response, 'Error al enviar el reporte por correo');
};

// === Nómina del centro: mismo motor que Personal → Nóminas por periodo, sin guardar ===
export const fetchReporteNominaCentro = async ({ idLavanderia, desde, hasta }) => {
  const params = new URLSearchParams({ lavanderiaId: idLavanderia, desde, hasta });
  const json = await handleResponse(
    await fetch(`${API_URL}/nomina/reporte-centro?${params}`),
    'No fue posible calcular la nómina del centro',
  );
  if (json?.state === false) throw new Error(json?.message || 'No fue posible calcular la nómina del centro');
  return json.data;
};

// === Análisis de nómina: todas las nóminas generadas en el rango (todas las plantas) ===
export const fetchAnalisisNomina = async ({ desde, hasta, centros = [] }) => {
  const params = new URLSearchParams({ desde, hasta });
  if (centros.length) params.set('centros', centros.join(','));
  const json = await handleResponse(
    await fetch(`${API_URL}/nomina/analisis?${params}`),
    'No fue posible cargar el análisis de nómina',
  );
  if (json?.state === false) throw new Error(json?.message || 'No fue posible cargar el análisis de nómina');
  return json.data;
};
